import React, { useMemo, useState } from "react";
import { Button, Chip, FormControl, TextField } from "@material-ui/core";

import { theme } from "../theme";
import validateEmail from "./validateEmail";

export type EmailChipsProps = {
  disabled?: boolean;
  emails: string[];
  onAdd: (email: string) => void;
  onDelete: (email: string) => void;
};

function EmailsChips({
  disabled = false,
  emails,
  onAdd,
  onDelete,
}: EmailChipsProps) {
  const [newEmail, setNewEmail] = useState<string>("");

  const newEmailErrors = useMemo(() => {
    const errors = validateEmail(newEmail);

    return errors.sort().join(", ");
  }, [newEmail]);

  const emailErrors = useMemo(() => {
    if (emails.length > 0) {
      return "";
    }

    return "At least one email is required.";
  }, [emails]);

  const emailInputHelperText = useMemo(() => {
    const errors = [newEmailErrors, emailErrors]
      .filter((e) => e !== "")
      .join(", ");

    if (errors !== "") {
      return errors;
    }

    if (newEmail !== "") {
      return "Press Enter or click Add to add email.";
    }
  }, [newEmail, newEmailErrors, emailErrors]);

  const handleAddEmail = () => {
    if (disabled) {
      return;
    }

    if (newEmailErrors.length > 0) {
      return;
    }

    onAdd(newEmail);
    setNewEmail("");
  };

  return (
    <FormControl>
      <div style={{ marginBottom: emails.length > 0 ? theme.spacing(2) : 0 }}>
        {emails.map((email) => (
          <Chip
            color="primary"
            component="span"
            key={`email-chips-email-${email}`}
            label={email}
            title={email}
            onDelete={() => {
              onDelete(email);
            }}
            style={{ marginRight: theme.spacing(1) }}
          />
        ))}
      </div>
      <div>
        <TextField
          disabled={disabled}
          error={emailErrors !== "" || newEmailErrors !== ""}
          helperText={emailInputHelperText}
          onChange={(e) => {
            setNewEmail(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter") {
              return;
            }

            handleAddEmail();
          }}
          placeholder="Add an Email..."
          size="small"
          style={{
            minWidth: theme.spacing(70),
          }}
          type="email"
          value={newEmail}
          variant="outlined"
        />
        <Button
          color="primary"
          disabled={disabled || newEmailErrors !== ""}
          onClick={() => {
            handleAddEmail();
          }}
          size="medium"
          style={{ marginLeft: theme.spacing(1) }}
          variant="contained"
        >
          Add
        </Button>
      </div>
    </FormControl>
  );
}

export default EmailsChips;
